import { z } from 'zod';
import type { AccountType, PayoutAccount } from 'api/escrow/types';

export function createPayoutAccountSchema(accountTypes: AccountType[]) {
  const slugs = accountTypes.map((t) => t.slug);

  return z
    .object({
      account_type: z.string().refine((v) => slugs.includes(v), {
        message: 'Invalid account type',
      }),
      channel_code: z.string().max(50).optional(),
      account_name: z.string().min(1, 'Account name is required').max(255),
      account_number: z.string().min(1, 'Account number is required').max(255),
    })
    .superRefine((data, ctx) => {
      const type = accountTypes.find((t) => t.slug === data.account_type);
      if (!type) return;

      // Multi-channel type requires channel_code
      if (type.channelCode === null && !data.channel_code) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          path: ['channel_code'],
          message: 'Please select a bank or channel',
        });
      }

      // Single-channel type must NOT send channel_code
      if (type.channelCode !== null && data.channel_code) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          path: ['channel_code'],
          message: 'Channel code should not be provided for this account type',
        });
      }

      // E-wallet account types require 0-prefixed mobile number
      if (type.channelCategory === 'EWALLET' && !/^09\d{9}$/.test(data.account_number)) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          path: ['account_number'],
          message: 'Please enter a valid Philippine mobile number (e.g., 09171234567)',
        });
      }

      // PayPal email validation
      if (type.slug === 'paypal_email' && !z.string().email().safeParse(data.account_number).success) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          path: ['account_number'],
          message: 'Please enter a valid email address',
        });
      }

      // PayPal phone validation
      if (type.slug === 'paypal_phone' && !/^\+\d{7,15}$/.test(data.account_number)) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          path: ['account_number'],
          message: 'Please enter an international phone number (e.g. +639171234567)',
        });
      }
    });
}

export type PayoutAccountFormValues = z.infer<ReturnType<typeof createPayoutAccountSchema>>;

/**
 * Return the account number field label and placeholder for the given account type slug.
 * Falls back to generic "Account number" for unknown slugs.
 */
export function getAccountNumberFieldMeta(slug: string | undefined): { label: string; placeholder: string } {
  switch (slug) {
    case 'gcash':
    case 'maya':
      return { label: 'Mobile number', placeholder: '09XXXXXXXXX' };
    case 'paypal_email':
      return { label: 'PayPal email address', placeholder: 'you@example.com' };
    case 'paypal_phone':
      return { label: 'PayPal phone number', placeholder: '+639171234567' };
    default:
      return { label: 'Account number', placeholder: 'Account number' };
  }
}

/**
 * Convert an existing PayoutAccount to form values for pre-population.
 */
export function toFormValues(account: PayoutAccount, accountTypes: AccountType[]): PayoutAccountFormValues {
  const type =
    accountTypes.find((t) => t.slug === account.accountType?.slug) ??
    accountTypes.find((t) => t.id === account.accountTypeId);

  return {
    account_type: type?.slug ?? account.accountType?.slug ?? '',
    account_name: account.accountName,
    account_number: account.accountNumber,
    ...(type?.channelCode === null ? { channel_code: account.channelCode ?? undefined } : {}),
  };
}
